/**
 * The S2 Overview map — the larger part of the splash card, and the thing every number
 * beside it is about.
 *
 * It replaces the §F4 splash mini-map (`MiniMap`), which was a 320 px choropleth of eleven
 * basins in the right-hand half of a card. The client's S2 specification makes the map the
 * page: *"Programme-wide map showing Soil Values countries and operational watersheds, with
 * relevant intervention boundaries and activity locations available for drill-down"*, with
 * filters on it and partnerships that appear only when the reader has gone in far enough to
 * see them. Its "80 % of the screen width" is measured against the card the client asked to
 * have back on 8 Sep 2026, not against the viewport; `base.css` carries the arithmetic.
 *
 * Four behaviours the specification asks for, and how each is met:
 *
 * 1. **First open** — the four countries with the intervention watersheds visible, at the
 *    programme extent. The basins are shaded by activity count, as R2 made them.
 * 2. **Partnerships are featured from first paint** (`partnershipsVisible`, which answers
 *    `true` unconditionally since 2026-09-12). S2 held them back until a watershed was
 *    picked or the reader zoomed past `PARTNERSHIP_ZOOM`; the client's instruction that
 *    afternoon was to *"feature the partnerships on the overview map"*, so all thirty-one
 *    are drawn at the programme extent and the map key names them. The geometry was always
 *    loaded up front — the third metric card reads 31 on first paint — so featuring them
 *    costs a layer, not a request. The selection still narrows the layer to the picked
 *    basin's own boundaries.
 * 3. **The most- and least-active watersheds are highlighted** — a thicker, darker ring on
 *    the busiest and a dashed grey one on the quietest, because a fill alone reads as "the
 *    top class of the ramp" rather than as "this one, specifically". They were labelled too
 *    until 2026-09-12, when the client asked for the two call-outs to come off the map;
 *    `data-activity` and the rings are what remain.
 * 4. **Clicking a country or a watershed selects it**, and the panel recounts. Panning and
 *    zooming report the extent up instead, debounced, so the counts follow the view without
 *    a request per frame.
 *
 * **A second, independent `L.Map`.** `features/map/mapContext` publishes the one instance
 * `MapView` owns for the routed Map Explorer, and this is not it: the Overview sits over the
 * live app and both maps exist at once. So this component owns its map, its tile layer and
 * its overlays, and disposes of all of them when the Overview closes.
 *
 * ---
 *
 * **R1 — the client's revision round** (8 Sep 2026). Five of its ten items are this map's:
 *
 * * **(5)** the choropleth key left the top-left chrome column and became `WatershedWidget`,
 *   anchored bottom-left and draggable;
 * * **(6)** the first-paint fit is the programme extent padded by 3 % of its own size rather
 *   than by `fitTo`'s 24 px, so it fills the frame without clipping the westernmost basin;
 * * **(7)** admin boundaries are a **line** (warm, no fill) against the watersheds' **area**
 *   (the activity ramp) — #659's split, with the roles swapped for this map's choropleth —
 *   and the activity count printed at every centroid is gone. The two extreme badges stay;
 * * **(8)** `Ticker` takes generated insights instead of fetching `/audit/ticker`;
 * * **(10)** the filter now removes geography, not just counts: `inSelection` filters the
 *   watershed layer, the outline layer and the partnership layer, so the map draws exactly
 *   what the panel counted.
 *
 * ---
 *
 * **R2 — the client's second round** (8 Sep 2026, evening). One item is this map's: the two
 * extreme labels became **call-outs**. They used to be pills centred on their basin's
 * centroid, which put a box of type flat on the shape whose fill it was there to explain; each
 * one now sits outside the polygon's box with a hairline leader running back to a dot on the
 * centroid. `callouts.ts` owns the arithmetic and is unit-tested without a map; this file
 * measures, writes the offsets, and re-places on `zoomend` / `moveend` / `resize`.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';

import {
  fetchActivityPoints,
  fetchUnits,
  queryKeys,
  type ActivityPointsFC,
  type UnitsFC,
} from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { escapeHtml } from '../../lib/html';
import { COUNTRY_NAMES, useAppStore, type Country } from '../../lib/state';
import { resolveBasemap, useBasemaps } from '../map/basemaps';
import { fitTo, HOME_BOUNDS } from '../map/panes';
import {
  markerCountry,
  markerTooltip,
  watershedActivity,
  watershedFill,
  watershedRows,
  watershedShading,
} from './activityPoints';
import {
  activityExtremes,
  inSelection,
  partnershipsVisible,
  type Bbox,
  type OverviewSelection,
  type TickerInsight,
} from './overview';
import { Ticker } from './Ticker';
import { WatershedWidget } from './WatershedWidget';

/** What `L.geoJSON` will take, without importing a `@types/geojson` we do not have. */
type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/**
 * How long the map is left alone after a pan or zoom before the panel recounts.
 *
 * The refresh rule is "in real time", and 300 ms is what real time means for a value that is
 * only readable once the reader has stopped moving: recomputing on every `move` event
 * repaints three cards and two charts sixty times a second for numbers nobody can read
 * mid-drag. `moveend`/`zoomend` alone would be simpler and worse — Leaflet fires them once
 * per inertia settle, and a flick that ends in three settles would recount three times.
 */
export const VIEW_DEBOUNCE_MS = 300;

/**
 * The country outlines — **a line, and only a line** (R1 item 7).
 *
 * The client's complaint was that the admin boundaries and the watersheds were the same mark:
 * both green, both a thin stroke over a wash, indistinguishable where they overlap at the
 * Nigeria cluster. The fix is the one #659 already made in the explorer — stop asking hue
 * alone to separate two *kinds* of thing:
 *
 * * **Admin = line.** A firm, warm, near-black `#4A3524` stroke with **no fill at all**, so a
 *   country border reads as a border and never tints the basin under it. Warm against the
 *   green ramp is the complementary half of the pair, and at 88 % on the light basemap it is
 *   4.9:1 against the palest class of the ramp.
 * * **Watershed = area.** The activity choropleth under a matching dark-green edge, below.
 *
 * The explorer's own admin row (`#22301F`, 2.2 px, `fill:false`) is the same treatment; the
 * hue is shifted warm here because this map has no third boundary type to confuse it with and
 * the green ramp is the thing it has to stay off.
 */
const OUTLINE_STYLE: L.PathOptions = {
  color: '#4A3524',
  weight: 1.9,
  opacity: 0.88,
  fill: false,
};

/** A basin's stroke, so the palest class of the ramp still reads as an object. */
const WATERSHED_STROKE: L.PathOptions = {
  color: '#14400F',
  weight: 1.3,
  opacity: 0.9,
  fillOpacity: 0.92,
};

/** The selected basin: the same shape, ringed so the reader can see what they picked. */
const WATERSHED_SELECTED: L.PathOptions = { color: '#14400F', weight: 3.4, opacity: 1 };

/**
 * The two extremes. Weight and dash rather than fill, because the fill is already carrying
 * the activity count — repainting it would overwrite the very number the badge names.
 */
const MOST_ACTIVE_STROKE: L.PathOptions = { color: '#14400F', weight: 3, opacity: 1 };
const LEAST_ACTIVE_STROKE: L.PathOptions = {
  color: '#8B958A',
  weight: 2.4,
  opacity: 1,
  dashArray: '5 3',
};

/** Partnership boundaries — a distinct hue from the basins, drawn as an outline only. */
const PARTNERSHIP_STYLE: L.PathOptions = {
  color: '#8E3B62',
  weight: 1.6,
  opacity: 0.95,
  fillColor: '#8E3B62',
  fillOpacity: 0.1,
};

/** Where a card click or a bar click asks the map to go. */
export type FitTarget =
  | { kind: 'countries' | 'watersheds' | 'partnerships' }
  | { kind: 'unit'; id: string };

export interface OverviewMapProps {
  selection: OverviewSelection;
  /** A click on a basin: adopt it as the selection (and its country with it). */
  onPickWatershed: (unitId: string, country: Country | null) => void;
  /** A click on a country outline. */
  onPickCountry: (country: Country) => void;
  /** The debounced view, for the extent-driven counts. `zoom` gates the partnerships. */
  onView: (view: Bbox, zoom: number) => void;
  /** Loaded geometry, published up so the panel counts the same features the map drew. */
  onData: (data: { watersheds?: UnitsFC; partnerships?: UnitsFC }) => void;
  /**
   * Counts by watershed, published up for the pathway chart's scope and the extremes —
   * together with the payload's own `unassigned`, which the panel's ticker reports and which
   * would otherwise need a second declaration of the same query.
   */
  onActivity: (rows: { unit_id: string; count: number }[], unassigned: number) => void;
  /** A fit request from the panel. `nonce` so clicking the same card twice fits twice. */
  fit: { target: FitTarget; nonce: number } | null;
  /**
   * R1 item 10: the watersheds a programme-pathway filter leaves standing, or `null` for no
   * pathway filter. Computed panel-side from the same `/activities/pathways` payload the
   * chart is drawn from, so the map and the bars scope to the same basins.
   */
  pathwayScope: ReadonlySet<string> | null;
  /** The bottom strip's rotation, generated from the panel's own numbers (item 8). */
  insights: readonly TickerInsight[];
  /**
   * Chrome that floats over the map — in practice the filter bar (user direction, 8 Sep
   * 2026: *"put the filters on the map"*).
   *
   * It arrives as children rather than as three `on…Change` props because the state the
   * filters write is the Overview's `selection`, which lives in `features/landing/index`
   * and drives eight derived values there. Lifting the controls into this component would
   * have meant lifting that state with them, or threading it back down; passing the markup
   * moves *where the bar is painted* and nothing else, which is the whole of the change.
   */
  children?: ReactNode;
}

export function OverviewMap({
  selection,
  onPickWatershed,
  onPickCountry,
  onView,
  onData,
  onActivity,
  fit,
  pathwayScope,
  insights,
  children,
}: OverviewMapProps) {
  const { t } = useTranslation();
  const slotRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const [zoom, setZoom] = useState<number>(0);
  const basemap = useAppStore((s) => s.basemap);
  const basemaps = useBasemaps();
  const country = selection.country;

  const { data: outlines } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z5'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z5'),
  });
  const { data: basins } = useQuery<UnitsFC>({
    // `z7` rather than the old mini-map's `z5`: this map is around a thousand pixels wide,
    // and the generalisation that was right for a 320 px panel shows its corners at that size.
    queryKey: queryKeys.units('watershed', null, 'ALL', 'z7'),
    queryFn: () => fetchUnits('watershed', null, 'ALL', 'z7'),
  });
  const showPartnerships = partnershipsVisible(zoom, selection);

  /**
   * Partnerships are **loaded on first paint and drawn only later**, which is not the same
   * thing and the difference is a requirement.
   *
   * S2 hides the boundaries at country zoom (`partnershipsVisible`) *and* asks the third
   * metric card to read 31 the moment the page opens — "counted within the current
   * selection/extent… from the loaded boundary data". A card cannot count a payload nobody
   * fetched, so deferring the request would have made "4 / 11 / 31" read "4 / 11 / 0" until
   * the reader zoomed in, which is checklist item 4 failing to buy a first-paint saving of
   * 31 polygons.
   */
  const { data: partnerships } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('partnership', null, 'ALL', 'z7'),
    queryFn: () => fetchUnits('partnership', null, 'ALL', 'z7'),
  });
  /*
   * How many partnerships the layer actually draws. The key shows its swatch only when there is
   * something to point at; the names it briefly listed came off on the client's instruction
   * (2026-09-12, third round) and the roll-call went with them.
   */
  const partnershipCount = useMemo(
    () => (partnerships?.features ?? []).length,
    [partnerships],
  );
  const { data: points } = useQuery<ActivityPointsFC>({
    queryKey: queryKeys.activityPoints('ALL', 'watershed'),
    queryFn: () => fetchActivityPoints('ALL', 'watershed'),
  });

  const allRows = useMemo(
    () => watershedRows(basins, watershedActivity(points)),
    [basins, points],
  );
  /**
   * R1 item 10 — *"when the user filters to an operational area, the other areas disappear
   * from the map"*, which is the explorer's own cascade rule (`hierarchy.scopedRows`) brought
   * to this map. `inSelection` is the predicate the cards count with, so what is drawn and
   * what is counted are the same set by construction rather than by agreement.
   */
  const rows = useMemo(
    () =>
      allRows.filter((row) =>
        inSelection(
          { id: row.unit_id, country: row.country, parent: null },
          selection,
          pathwayScope,
        ),
      ),
    [allRows, selection, pathwayScope],
  );
  /**
   * The ramp is classified over **every** basin, not over the filtered ones: a basin that
   * keeps its colour when its neighbours are filtered away is a legend a reader can trust,
   * and re-quantiling three shapes into five classes says nothing about the programme.
   */
  const shading = useMemo(() => watershedShading(allRows.map((r) => r.count)), [allRows]);
  const extremes = useMemo(
    () => activityExtremes(rows.map((r) => ({ unit_id: r.unit_id, count: r.count }))),
    [rows],
  );

  // --- publish what was loaded, so the panel counts the map's own features ----
  useEffect(() => {
    // The panel counts what was *loaded*, not what is currently painted: see the
    // partnerships query above for why those are two different sets.
    onData({ watersheds: basins, partnerships });
  }, [basins, partnerships, onData]);

  useEffect(() => {
    // The **unfiltered** rows: the panel applies its own scope to them, and publishing the
    // scoped set would apply the filter twice.
    onActivity(
      allRows.map((r) => ({ unit_id: r.unit_id, count: r.count })),
      points?.unassigned ?? 0,
    );
  }, [allRows, points?.unassigned, onActivity]);

  // --- the map itself, created once ------------------------------------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;
    /*
     * `zoomSnap: 0` — R1 item 6, and the half of it `pad(0.03)` could not deliver.
     *
     * `fitBounds` computes the fractional zoom that frames the bounds and then rounds it
     * **down** to a multiple of `zoomSnap`, so a 0.25 step throws away up to 2^0.25 — 19 % of
     * the linear scale — every time. On the programme extent it threw away almost all of it:
     * the exact fit is z5.98 and the map opened at z5.75, which left the eleven basins
     * spanning 80 % of a frame the client had just asked them to fill. Padding cannot recover
     * that; only the snap can, because the snap is what discards it.
     *
     * 0 rather than a smaller step: this map is a display surface with no tile-alignment
     * requirement of its own, and every zoom it takes is a `fitBounds` to a payload's bounds
     * rather than a reader's click. `zoomDelta` is untouched, so the ± control still moves in
     * whole levels.
     */
    const m = L.map(el, { zoomControl: false, attributionControl: true, zoomSnap: 0 });
    m.attributionControl.setPrefix('Soil Values Geoportal');
    L.control.zoom({ position: 'bottomright' }).addTo(m);
    m.fitBounds(HOME_BOUNDS);
    mapRef.current = m;
    setMap(m);
    setZoom(m.getZoom());
    return () => {
      m.remove();
      mapRef.current = null;
      setMap(null);
    };
  }, []);

  /**
   * Leaflet reads the container size once, at construction, and the Overview's grid has
   * frequently not given this element a height on the first paint after mount — which leaves
   * the map convinced it is 0×0 and drawing no tiles at all.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!map || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [map]);

  // --- the extent, debounced, and the zoom, immediately ----------------------
  useEffect(() => {
    if (!map) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const publish = () => {
      const b = map.getBounds();
      onView([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], map.getZoom());
    };
    const onMove = () => {
      // The zoom is not debounced: it decides whether the partnership layer exists at all,
      // and a third of a second of a basin without its boundaries reads as a bug.
      setZoom(map.getZoom());
      clearTimeout(timer);
      timer = setTimeout(publish, VIEW_DEBOUNCE_MS);
    };
    map.on('move zoom', onMove);
    publish();
    return () => {
      clearTimeout(timer);
      map.off('move zoom', onMove);
    };
  }, [map, onView]);

  // --- basemap, kept in sync with the store ----------------------------------
  useEffect(() => {
    if (!map) return;
    const def = resolveBasemap(basemaps, basemap);
    const layer = L.tileLayer(def.url, { attribution: def.attr, maxZoom: def.maxZoom }).addTo(map);
    layer.bringToBack();
    return () => {
      map.removeLayer(layer);
    };
  }, [map, basemap, basemaps]);

  // --- country outlines ------------------------------------------------------
  /**
   * R1 item 10, at the admin level. The rule is the explorer's `scopedRows`: **nothing above
   * the scope survives.** So a country filter leaves that country's outline and drops the
   * other three, and a watershed selection drops the admin layer entirely — a national border
   * drawn around one basin is not context, it is the unfiltered map still showing through.
   * A pathway filter is not an admin scope and leaves the borders alone.
   */
  useEffect(() => {
    if (!map || !outlines) return;
    if (selection.watershed !== null) return;
    const layer = L.geoJSON(outlines as unknown as GeoJsonInput, {
      filter: (feature) => {
        const props = (feature?.properties ?? {}) as { country?: string };
        return selection.country === 'ALL' || props.country === selection.country;
      },
      style: () => OUTLINE_STYLE,
      onEachFeature: (feature, shape) => {
        const props = (feature?.properties ?? {}) as { id?: string; country?: string };
        const iso3 = markerCountry(props);
        shape.on('add', () => {
          const el = shape instanceof L.Path ? shape.getElement() : null;
          el?.setAttribute('data-country', String(props.country ?? ''));
          el?.classList.add('ov-outline');
        });
        // S2 item 4: "Clicking a country or watershed selects it."
        if (iso3) shape.on('click', () => onPickCountry(iso3));
      },
    }).addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, outlines, onPickCountry, selection.country, selection.watershed]);

  // --- watershed choropleth, its labels, and the two extremes ----------------
  useEffect(() => {
    if (!map || !basins) return;
    const group = L.layerGroup().addTo(map);
    const byId = new Map(rows.map((r) => [r.unit_id, r]));

    const tooltipFor = (unitId: string): string => {
      const row = byId.get(unitId);
      if (!row) return '';
      const countryName = row.country
        ? t(`country.${row.country}`, COUNTRY_NAMES[row.country as Country] ?? row.country)
        : '';
      return markerTooltip(
        t('splash.map.markerCount', { count: row.count, value: fmtNum(row.count, 0) }),
        row.name,
        countryName,
      );
    };

    const pick = (unitId: string) => {
      const row = byId.get(unitId);
      onPickWatershed(unitId, row ? markerCountry(row) : null);
    };

    const strokeFor = (unitId: string): L.PathOptions => {
      if (selection.watershed === unitId) return WATERSHED_SELECTED;
      if (extremes.most === unitId) return MOST_ACTIVE_STROKE;
      if (extremes.least === unitId) return LEAST_ACTIVE_STROKE;
      return {};
    };

    /**
     * The drawn polygon per basin, so a call-out can measure the shape it points at.
     * `L.Polyline` rather than `L.Path`: `getBounds` is the polyline's, and a polygon is one.
     */
    const shapesById = new Map<string, L.Polyline>();

    const shapes = L.geoJSON(basins as unknown as GeoJsonInput, {
      // R1 item 10: only the basins the filter left standing are drawn at all. `byId` is
      // built from the scoped rows, so this is the same set the panel counted.
      filter: (feature) => byId.has(String((feature?.properties as { id?: string })?.id ?? '')),
      style: (feature) => {
        const id = String((feature?.properties as { id?: string })?.id ?? '');
        return {
          ...WATERSHED_STROKE,
          ...strokeFor(id),
          fillColor: watershedFill(byId.get(id)?.count ?? 0, shading),
          // A selection dims everything it is not, so "these numbers are about that shape"
          // is visible rather than inferred from the panel.
          fillOpacity:
            selection.watershed && selection.watershed !== id ? 0.3 : WATERSHED_STROKE.fillOpacity,
        };
      },
      onEachFeature: (feature, shape) => {
        const id = String((feature?.properties as { id?: string })?.id ?? '');
        if (!byId.has(id)) return;
        if (shape instanceof L.Polyline) shapesById.set(id, shape);
        shape.bindTooltip(escapeHtml(tooltipFor(id)), { className: 'unit-tip', sticky: true });
        shape.on('click', () => pick(id));
        shape.on('add', () => {
          const el = shape instanceof L.Path ? shape.getElement() : null;
          if (!el) return;
          el.setAttribute('data-unit', id);
          el.setAttribute('data-count', String(byId.get(id)?.count ?? 0));
          if (extremes.most === id) el.setAttribute('data-activity', 'most');
          if (extremes.least === id) el.setAttribute('data-activity', 'least');
          if (selection.watershed === id) el.setAttribute('data-selected', '1');
          el.classList.add('ws-shape');
        });
      },
    }).addTo(group);
    shapes.bringToFront();

    /*
     * **The call-outs are gone** (client, 2026-09-12): *"we take off the text most activities
     * and least activities from the map"*, and the same two words came off the panel with
     * them. R2 item 1 had placed them off their basin with a leader line; the instruction
     * now is not to place them better but not to place them at all.
     *
     * `data-activity` stays on the polygons. It is the ring the extremes still carry — the
     * highlight S2 item 3 asked for — and it is what the e2e identifies them by. Only the
     * words are withdrawn. `callouts.ts` and its tests are left in place: the arithmetic is
     * sound and unused, and deleting it would cost more to restore than to keep.
     */

    return () => {
      map.removeLayer(group);
    };
  }, [map, basins, rows, shading, extremes, selection.watershed, onPickWatershed, t]);

  // --- partnerships, only once the reader is in far enough --------------------
  /**
   * **Drawn for the selection, not for the whole programme.** S2 item 2 is "select a
   * watershed → *its* partnerships appear", and the payload is fetched once for all four
   * countries (so the third card can read 31 on first paint without a request). Those two
   * facts only agree if the layer filters on the way to the map: without it, picking Kou
   * painted all thirty-one boundaries across Burkina, Mali, Niger and Nigeria, and the card
   * beside them said a different number.
   *
   * `inSelection` is the same predicate the cards count with — deliberately the one
   * function, because the map and the panel disagreeing about which shapes are in scope is
   * exactly the bug this replaced.
   */
  useEffect(() => {
    if (!map || !showPartnerships || !partnerships) return;
    const layer = L.geoJSON(partnerships as unknown as GeoJsonInput, {
      filter: (feature) => {
        const props = (feature?.properties ?? {}) as {
          id?: string;
          country?: string | null;
          parent_id?: string | null;
        };
        return inSelection(
          {
            id: String(props.id ?? ''),
            country: props.country ?? null,
            parent: props.parent_id ?? null,
          },
          selection,
          pathwayScope,
        );
      },
      style: () => PARTNERSHIP_STYLE,
      /*
       * **Decorative, and that is load-bearing** (2026-09-12). Featuring the partnerships put
       * 31 filled polygons permanently on top of the basins — `bringToFront` below — and an
       * interactive layer there swallows the pointer: hovering a basin opened the
       * partnership's tooltip instead, and clicking one to filter could miss. Basin picking is
       * the primary gesture on this map and the client did not trade it away for a decoration.
       *
       * So the layer takes no events, and its tooltip goes with them — a tooltip on a shape
       * that cannot be hovered is dead code. The names it carried are not lost: they are the
       * key the same instruction asked for, in `WatershedWidget`, where all 31 are readable at
       * once instead of one hover at a time.
       */
      interactive: false,
      onEachFeature: (feature, shape) => {
        const props = (feature?.properties ?? {}) as { id?: string; name?: string };
        shape.on('add', () => {
          const el = shape instanceof L.Path ? shape.getElement() : null;
          el?.setAttribute('data-partnership', String(props.id ?? ''));
          el?.classList.add('pt-shape');
        });
      },
    }).addTo(map);
    layer.bringToFront();
    return () => {
      map.removeLayer(layer);
    };
  }, [map, showPartnerships, partnerships, selection, pathwayScope]);

  // --- the programme extent on first paint, and a re-frame per country -------
  const framedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!map || !basins) return;
    if (framedFor.current === country) return;
    /*
     * **The country extent** (client, 2026-09-12: *"the map zoom in level should be the country
     * extent"*). This replaces the same morning's union-of-all-layers frame, which was itself a
     * reading of *"see all the layers at full extent"* — the client has now said which extent
     * they meant, and the countries are the outer one: every basin and every partnership sits
     * inside a programme country, so framing the countries frames all of it with room to spare
     * and gives the four shapes the reader recognises rather than a ragged union of basins.
     *
     * Falls back to the basins if the outline layer has not landed — it is a separate request,
     * and a first paint that waited for it would show an unframed world map instead.
     */
    const frame = L.latLngBounds([]);
    const addLayer = (fc: UnitsFC | undefined) => {
      if (!fc) return;
      const b = L.geoJSON(fc as unknown as GeoJsonInput, {
        filter: (feature) => {
          const props = (feature?.properties ?? {}) as { country?: string };
          return country === 'ALL' || props.country === country;
        },
      }).getBounds();
      if (b.isValid()) frame.extend(b);
    };
    addLayer(outlines);
    if (!frame.isValid()) addLayer(basins);
    const bounds = frame;
    map.invalidateSize({ animate: false });
    /*
     * **Unpadded** (R1 item 6): *"Consider zooming out the map to fill screen as default
     * view."* `fitTo`'s default is `FIT_PADDING` — 24 px on every side, which is right for the
     * explorer, where the sidebar and the dock crowd the viewport, and wrong here: this map is
     * three fifths of a card and the 24 px cost most of a zoom step, leaving the programme
     * floating in a frame it did not fill. Nothing is lost to the edges, because the chrome
     * over this map is pinned to its corners and not to the geometry.
     */
    // `pad(0.03)` rather than `fitTo`'s pixel padding: 3 % of the extent's own size keeps the
    // westernmost basin and its badge off the frame's edge at any zoom, and — unlike a fixed
    // 24 px inset — costs nothing at the widths this card is actually drawn at.
    fitTo(map, bounds.isValid() ? bounds.pad(0.03) : HOME_BOUNDS, false);
    framedFor.current = country;
  }, [map, basins, outlines, country]);

  // --- fit requests from the panel (a card click, a target bar click) --------
  /**
   * **Every card fits to what that card counted.** S2 §1: "clicking Watersheds zooms to fit
   * all watersheds *in the current selection*". So all three branches ask `inSelection`, the
   * same predicate the card's own number came from — a card that reads 3 and then frames
   * eleven basins has answered a question nobody asked.
   */
  useEffect(() => {
    if (!map || !fit) return;
    const collect = (
      payload: UnitsFC | undefined,
      keep: (props: { id: string; country: string | null; parent: string | null }) => boolean,
    ) =>
      L.geoJSON(payload as unknown as GeoJsonInput, {
        filter: (feature) => {
          const props = (feature?.properties ?? {}) as {
            id?: string;
            country?: string | null;
            parent_id?: string | null;
          };
          return keep({
            id: String(props.id ?? ''),
            country: props.country ?? null,
            parent: props.parent_id ?? null,
          });
        },
      }).getBounds();
    const scoped = (props: { id: string; country: string | null; parent: string | null }) =>
      inSelection(props, selection);

    let bounds: L.LatLngBounds | null = null;
    if (fit.target.kind === 'unit') {
      const id = fit.target.id;
      const found = [basins, partnerships, outlines]
        .map((payload) => collect(payload, (props) => props.id === id))
        .find((box) => box.isValid());
      bounds = found ?? null;
    } else if (fit.target.kind === 'partnerships') {
      bounds = collect(partnerships, scoped);
    } else if (fit.target.kind === 'watersheds') {
      bounds = collect(basins, scoped);
    } else {
      // The country outlines are the one payload `inSelection` cannot read: an outline's own
      // `id` is `BF` while the filter speaks ISO-3, and it has no watershed parent. Its
      // scope is the country filter alone, and a watershed selection narrows it to that
      // basin's country rather than to a shape the outline layer does not contain.
      const wanted =
        selection.country !== 'ALL'
          ? selection.country
          : (basins?.features ?? []).find(
              (f) => (f.properties as { id?: string })?.id === selection.watershed,
            )?.properties?.country ?? null;
      bounds = collect(outlines, (props) => !wanted || props.country === wanted);
    }
    if (bounds?.isValid()) fitTo(map, bounds);
    else fitTo(map, HOME_BOUNDS);
    // `fit.nonce` is in the dependency list on purpose: clicking the same card twice must
    // fit twice, and an object identical to the last one would otherwise be a no-op.
  }, [map, fit?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div id="landMapSlot" ref={slotRef}>
      <div id="homemap" ref={containerRef} data-testid="overview-map" />
      {/*
        The map's top-left chrome: the filter bar the client asked to have on the map.

        The choropleth key used to be the second row of this column. R1 item 5 moved it to the
        bottom-left as its own draggable widget, so the column now holds one thing — kept as a
        column rather than collapsed into the bar, because `pointer-events:none` here with
        `auto` on the child is what keeps the map pannable everywhere the bar is not.
      */}
      <div className="ov-map-chrome" data-testid="overview-map-chrome">
        {children}
      </div>
      <WatershedWidget
        bounds={slotRef}
        rows={rows}
        shading={shading}
        partnershipColor={String(PARTNERSHIP_STYLE.fillColor ?? '#8E3B62')}
        partnershipCount={partnershipCount}
      />
      <Ticker insights={insights} />
    </div>
  );
}

export default OverviewMap;
